import { useState } from 'react';
import toast from 'react-hot-toast';

import { Modal, Select } from 'components/ui';
import { useAuth, useTheme } from 'hooks';
import { cx } from 'utils';
import { TIMEZONES } from 'features/admin/constants';
import { SunIcon, MoonIcon, MonitorIcon } from './icons';

/**
 * SettingsModal — account settings dialog.
 *   • Appearance — Light / Dark / System (persists via ThemeContext).
 *   • Timezone   — the user's personal zone; class times across the app render
 *                  in it, so a student and mentor each see sessions in their own
 *                  local time. Persists to the user's profile.
 */
const THEME_OPTIONS = [
  { value: 'light', label: 'Light', hint: 'Bright canvas', Icon: SunIcon },
  { value: 'dark', label: 'Dark', hint: 'Easy on the eyes', Icon: MoonIcon },
  { value: 'system', label: 'System', hint: 'Match your device', Icon: MonitorIcon },
];

export default function SettingsModal({ open, onClose }) {
  const { preference, setPreference } = useTheme();
  const { profile, updateProfile } = useAuth();
  const [tz, setTz] = useState(profile?.timezone || '');
  const [savingTz, setSavingTz] = useState(false);

  const handleTimezone = async (value) => {
    setTz(value);
    setSavingTz(true);
    try {
      await updateProfile({ timezone: value });
      toast.success('Timezone updated.');
    } catch {
      toast.error('Could not update timezone. Please try again.');
      setTz(profile?.timezone || '');
    } finally {
      setSavingTz(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Settings" description="Personalize how LearnX looks." size="md">
      <section>
        <h3 className="text-sm font-semibold text-slate-900">Appearance</h3>
        <p className="mt-0.5 text-xs text-slate-500">Choose a theme for the LearnX interface.</p>

        <div className="mt-3 grid grid-cols-3 gap-2.5">
          {THEME_OPTIONS.map(({ value, label, hint, Icon }) => {
            const active = preference === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => setPreference(value)}
                aria-pressed={active}
                className={cx(
                  'group flex flex-col items-center gap-2 rounded-2xl border p-3.5 text-center transition-all duration-200 ease-premium',
                  active
                    ? 'border-brand-500 bg-brand-50 shadow-soft ring-1 ring-brand-500'
                    : 'border-slate-200 bg-white hover:border-brand-200 hover:bg-brand-50/50'
                )}
              >
                <span
                  className={cx(
                    'flex h-10 w-10 items-center justify-center rounded-xl transition-colors',
                    active ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-500'
                  )}
                >
                  <Icon className="h-[18px] w-[18px]" aria-hidden />
                </span>
                <span className="text-sm font-semibold text-slate-900">{label}</span>
                <span className="text-2xs font-medium text-slate-500">{hint}</span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="mt-6 border-t border-slate-100 pt-6">
        <h3 className="text-sm font-semibold text-slate-900">Timezone</h3>
        <p className="mt-0.5 text-xs text-slate-500">
          Session times across LearnX show in this zone.
        </p>

        <div className="mt-3">
          <Select
            value={tz}
            onChange={(e) => handleTimezone(e.target.value)}
            placeholder="Select your timezone"
            options={TIMEZONES}
            disabled={savingTz}
            aria-label="Your timezone"
          />
        </div>
      </section>
    </Modal>
  );
}
